import { computed, getCurrentInstance, watch } from 'vue'

import type { ExtractPropTypes } from 'vue'
import type { TransferPanelState, Key} from './transfer'

export const CHECKED_CHANGE_EVENT = 'checked-change'

export const useCheckProps = {
  data: {
    type: Array,
    default() {
      return []
    },
  },
  optionRender: Function,
  title: String,
  filterable: Boolean,
  filterMethod: Function,
  defaultChecked: Array,
  props: Object,
  noDataText: String,
  listHeight: Number,
  listWidth: Number
}

export const useCheck = (
  props: ExtractPropTypes<typeof useCheckProps>,
  panelState: TransferPanelState
) => {
  const { emit } = getCurrentInstance()!

  const labelProp = computed(() => props.props!.label || 'label')

  const keyProp = computed(() => props.props!.key || 'key')

  const disabledProp = computed(() => props.props!.disabled || 'disabled')

  const filteredData = computed<any[]>(() => {
    return props.data.filter((item:any) => {
      if (typeof props.filterMethod === 'function') {
        return props.filterMethod(panelState.query, item)
      } else {
        const label = item[labelProp.value] || item[keyProp.value].toString()
        return label.toLowerCase().includes(panelState.query.toLowerCase())
      }
    })
  })

  const checkableData = computed(() => {
    return filteredData.value.filter((item:any) => !item[disabledProp.value])
  })


  const isIndeterminate = computed(() => {
    const checkedLength = panelState.checked.length
    return checkedLength > 0 && checkedLength < checkableData.value.length
  })

  const checkedLength = computed(() => {
      return  panelState.checked.length
  })

  const panelStyle = computed(() => {
    return props.listWidth ? 'width: auto' : ''
  })

  const bodyStyle = computed(() => {
    return {
      width: `${props.listWidth}px`,
      height: props.filterable && props.listHeight ? `${props.listHeight + 62}px` :`${props.listHeight}px`
    }
  })

  const listStyle = computed(() => {
    return props.listHeight
      ? props.filterable
        ? 'height: calc(100% - 62px)'
        : 'height: 100%'
      : ''
  })

  const updateAllChecked = () => {
    const checkableDataKeys = checkableData.value.map(
      (item:any) => item[keyProp.value]
    )
    panelState.allChecked =
      checkableDataKeys.length > 0 &&
      checkableDataKeys.every((item) => panelState.checked.includes(item))
  }

  const handleAllCheckedChange = (value: Key[]) => {
    panelState.checked = value
      ? checkableData.value.map((item:any) => item[keyProp.value])
      : []
  }

  const handelInput = (value) => {
    emit('on-input', value)
  }

  const handelClear = () => {
    emit('on-clear')
  }

  watch(
    () => panelState.checked,
    (val, oldVal) => {
      updateAllChecked()

      if (panelState.checkChangeByUser) {
        const movedKeys = val
          .concat(oldVal)
          .filter((v) => !val.includes(v) || !oldVal.includes(v))
        emit(CHECKED_CHANGE_EVENT, val, movedKeys)
      } else {
        emit(CHECKED_CHANGE_EVENT, val)
        panelState.checkChangeByUser = true
      }
    }
  )

  watch(checkableData, () => {
    updateAllChecked()
  })

  watch(
    () => props.data,
    () => {
      const checked = []
      const filteredDataKeys = filteredData.value.map(
        (item:any) => item[keyProp.value]
      )
      panelState.checked.forEach((item) => {
        if (filteredDataKeys.includes(item)) {
          checked.push(item as never)
        }
      })
      panelState.checkChangeByUser = false
      panelState.checked = checked
    }
  )

  watch(
    () => props.defaultChecked,
    (val, oldVal) => {
      if (
        oldVal &&
        val!.length === oldVal.length &&
        val!.every((item) => oldVal.includes(item))
      )
        return

      const checked = []
      const checkableDataKeys = checkableData.value.map(
        (item:any) => item[keyProp.value]
      )

      val!.forEach((item) => {
        if (checkableDataKeys.includes(item)) {
          checked.push(item as never)
        }
      })
      panelState.checkChangeByUser = false
      panelState.checked = checked
    },
    {
      immediate: true,
    }
  )

  return {
    labelProp,
    keyProp,
    disabledProp,
    checkedLength: checkedLength,
    filteredData,
    checkableData,
    isIndeterminate,
    updateAllChecked,
    handleAllCheckedChange,
    handelInput,
    handelClear,
    panelStyle,
    bodyStyle,
    listStyle,
  }
}
